// Type-aware JSON operator emission for the IntellisenseEngine.
//
// Slice C3 of `Ralph/JsonColumnSemantics/PRD.md`. The per-engine resolvers
// now gate JSON-operator suggestions on whether the cursor's preceding
// operand is a JSON-classified column. When it is, we call SR-RO's
// `suggestOperatorsForOperand` to get the engine-correct operator list and
// emit them as tier-6 (snippet) completion candidates.
//
// The legacy `emitJsonbOperators` / `emitJsonOperators` paths (which
// emitted unconditionally) are replaced by this helper. When the operand
// is not JSON-classified (numeric column, text column without JSON marker,
// or no operand visible at all), we emit nothing — strictly more
// conservative than v0's over-surfacing posture.

import type { CompletionCandidate, CompletionPositionKind } from '@/core/engines';
import type { DatabaseEngineId } from '@/core/engines/engineTypes';
import type { Operator } from '@/features/sql-resolver/json/operators/types';
import { suggestOperatorsForOperand } from '@/features/sql-resolver/json/operators/suggestOperators';
import { columnSqlTypeFromDataType } from '@/features/sql-resolver/json/operators/columnSqlType';
import { invalidStandardOperatorsForColumnType } from '@/features/sql-resolver/json/operators/typeAwareOperatorGating';
import type { JsonClassification } from '@/features/sql-resolver/types/schema';
import type { IdentifierRef, SqlEngine } from '@/features/sql-grammar/model';
import type { OperandLiteralSqlType, OperandType } from '@/features/sql-resolver/types/queryStructure';

/**
 * `DatabaseEngineId` (IntellisenseEngine layer) → `SqlEngine` (SR-RO /
 * SqlGrammar layer). The two differ only for PG: the renderer uses
 * `'pgsql'` historically while the grammar / resolver layer uses
 * `'postgres'`. All other engines share IDs.
 */
function toSqlEngine(engine: DatabaseEngineId): SqlEngine {
  switch (engine) {
    case 'pgsql':
      return 'postgres';
    case 'mssql':
    case 'mysql':
    case 'oracle':
    case 'sqlite':
      return engine;
    default:
      // Unsupported engines (e.g. a future `redshift`) — return a placeholder
      // and let the caller's suggestion list stay empty. Today this branch
      // is unreachable because the resolver enum is closed.
      return 'postgres';
  }
}

const SYNTHETIC_REF: IdentifierRef = {
  qualified: { name: '', raw: '', quoted: false, foldedName: '' },
  range: { startOffset: 0, endOffset: 0, startLine: 0, startColumn: 0, endLine: 0, endColumn: 0 },
  kind: 'column',
};

/**
 * Emit JSON-aware operator candidates for the resolver's output. When the
 * operand's classification is `not-json` or undefined, returns 0 emissions —
 * the engine-agnostic standard operator lattice (numeric / text / boolean)
 * is owned by the keyword / built-in completion paths and is not duplicated
 * here.
 */
export function emitJsonOperatorCandidates(
  out: CompletionCandidate[],
  jsonClassification: JsonClassification | undefined,
  engine: DatabaseEngineId,
): void {
  if (!jsonClassification || jsonClassification.kind === 'not-json') return;
  const operand: OperandType = {
    kind: 'column',
    ref: SYNTHETIC_REF,
    jsonClassification,
  };
  const operators = suggestOperatorsForOperand(operand, toSqlEngine(engine));
  for (const op of operators) {
    if (isStandardComparison(op.token)) continue;
    out.push(operatorToCandidate(op));
  }
}

/**
 * Type-aware operator emission for the operand immediately left of the cursor.
 *
 * - JSON-classified column → emit the distinctive JSON operators (delegates to
 *   `emitJsonOperatorCandidates`); the standard keyword operators stay as the
 *   keyword path produced them.
 * - Non-JSON column → prune the standard operator *keywords* the keyword path
 *   already pushed that are semantically invalid for the column's SQL type
 *   (e.g. `LIKE` after an integer column, range/order ops after a boolean).
 *   This is what closes the VQE↔autocomplete asymmetry: VQE never offered
 *   `LIKE` on a numeric column, the completion list now matches.
 *
 * Pruning is keyword-only (`kind: 'keyword'`) and keyed on a *confident* type
 * — `unknown`/`text` suppress nothing — so columns named `like`/`between`, JSON
 * snippet candidates, and ambiguous types (dates, uuid, blobs) are untouched.
 *
 * `positionKind` (optional) — when the cursor sits at a *predicate* slot
 * (`WHERE` / `HAVING` / `JOIN ON`) the non-JSON arm additionally **emits** the
 * positive standard comparison operators the column's type supports (`=`, `<>`,
 * `<`, `>`, `<=`, `>=`, `BETWEEN`, `IN`, `IS [NOT] NULL`) at tier 1 — see
 * `emitStandardComparisonOperatorsForColumn`. Omitted / non-predicate positions
 * keep the prune-only behaviour (back-compat: callers that pass 4 args are
 * unchanged), so the operators never leak into SELECT lists or function args.
 */
export function emitTypeAwareOperators(
  out: CompletionCandidate[],
  jsonClassification: JsonClassification | undefined,
  dataType: string | undefined,
  engine: DatabaseEngineId,
  positionKind?: CompletionPositionKind,
): void {
  if (jsonClassification && jsonClassification.kind !== 'not-json') {
    emitJsonOperatorCandidates(out, jsonClassification, engine);
    // A JSON column is still an ordinary operand for the comparisons its type
    // supports. Without this the distinctive JSON operators were the ONLY
    // tier-1 offer and `IS NULL` / `=` — which is what a filter on a JSON
    // column most often starts with — never surfaced (`=` is not a keyword, so
    // the keyword path cannot produce it either).
    if (isPredicateOperatorPosition(positionKind)) {
      emitJsonColumnComparisonOperators(out, jsonClassification, dataType, engine);
    }
    return;
  }
  pruneTypeInvalidOperatorKeywords(out, columnSqlTypeFromDataType(dataType));
  if (isPredicateOperatorPosition(positionKind)) {
    emitStandardComparisonOperatorsForColumn(out, dataType, engine);
  }
}

/**
 * Expression-operand counterpart of `emitTypeAwareOperators` for the path that
 * resolved a typed *expression* result (`CAST(x AS INT) ▮`, `UPPER(c) ▮`)
 * rather than a column. Prunes the type-invalid operator keywords and — at a
 * predicate slot — emits the positive standard comparison operators for the
 * inferred result type.
 */
export function emitTypeAwareExpressionOperators(
  out: CompletionCandidate[],
  resultSqlType: OperandLiteralSqlType,
  engine: DatabaseEngineId,
  positionKind?: CompletionPositionKind,
): void {
  pruneTypeInvalidOperatorKeywords(out, resultSqlType);
  if (isPredicateOperatorPosition(positionKind)) {
    emitStandardComparisonOperatorsForType(out, resultSqlType, engine);
  }
}

/**
 * Predicate slots where a comparison operator legitimately follows a completed
 * operand. `findPrecedingColumnOperand`'s boundary check also accepts commas
 * and open-parens (SELECT lists, function args) where operators must NOT be
 * offered, so positive emission is gated on the clause kind instead.
 */
export function isPredicateOperatorPosition(
  positionKind: CompletionPositionKind | undefined,
): boolean {
  return positionKind === 'where' || positionKind === 'having' || positionKind === 'join-on';
}

/**
 * Remove standard operator *keyword* candidates from `out` that are
 * semantically invalid for `sqlType` (e.g. `LIKE` after an integer/expression,
 * range/order ops after a boolean). Shared by the column path
 * (`emitTypeAwareOperators`) and the expression path (Slice 3a). Keyword-only +
 * confident-type-only, so columns named `like`, JSON snippets, and ambiguous
 * types are never touched.
 */
export function pruneTypeInvalidOperatorKeywords(
  out: CompletionCandidate[],
  sqlType: OperandLiteralSqlType,
): void {
  const invalid = invalidStandardOperatorsForColumnType(sqlType);
  if (invalid.size === 0) return;
  for (let i = out.length - 1; i >= 0; i -= 1) {
    const c = out[i];
    if (c.kind !== 'keyword') continue;
    if (invalid.has(c.label.toUpperCase())) out.splice(i, 1);
  }
}

/**
 * Emit the type-appropriate STANDARD comparison operators for a non-JSON
 * *column* operand at a predicate slot. This is the positive counterpart to
 * `pruneTypeInvalidOperatorKeywords`: rather than only stripping wrong operators
 * from the keyword list, it surfaces the full set the column's type actually
 * supports — symbols included (`=`, `<>`, `<`, `>`, `<=`, `>=`) plus the word
 * operators (`BETWEEN`, `IN`, `IS [NOT] NULL`) — at tier 1, where the next token
 * after `WHERE col ▮` actually goes. Numeric columns drop `LIKE`; booleans
 * collapse to equality + null. Routes through the same per-type lattice VQE uses
 * (`suggestOperatorsForOperand`), so the completion list matches the dropdown.
 */
export function emitStandardComparisonOperatorsForColumn(
  out: CompletionCandidate[],
  dataType: string | undefined,
  engine: DatabaseEngineId,
): void {
  emitStandardOperators(
    out,
    suggestOperatorsForOperand(
      { kind: 'column', ref: SYNTHETIC_REF, columnSqlType: columnSqlTypeFromDataType(dataType) },
      toSqlEngine(engine),
    ),
  );
}

/**
 * The comparisons a JSON-classified column supports. Every engine allows the
 * null tests. Equality depends on the column's STORAGE, not on the fact that it
 * holds JSON: a text-backed column (`check-isjson`, either loose kind) is an
 * ordinary string and compares normally, while a NATIVE JSON type may have no
 * equality operator at all — Postgres `json` has none (only `jsonb` does) and
 * Oracle's `JSON` type is not comparable, so offering `=` there would produce a
 * statement the engine rejects.
 */
function emitJsonColumnComparisonOperators(
  out: CompletionCandidate[],
  jsonClassification: JsonClassification,
  dataType: string | undefined,
  engine: DatabaseEngineId,
): void {
  const allowed = jsonComparisonTokens(jsonClassification, dataType, engine);
  emitStandardOperators(
    out,
    suggestOperatorsForOperand(
      { kind: 'column', ref: SYNTHETIC_REF, columnSqlType: 'unknown' },
      toSqlEngine(engine),
    ).filter((op) => allowed.has(op.token.toUpperCase())),
  );
}

function jsonComparisonTokens(
  jsonClassification: JsonClassification,
  dataType: string | undefined,
  engine: DatabaseEngineId,
): ReadonlySet<string> {
  const tokens = new Set(['IS NULL', 'IS NOT NULL']);
  if (isIncomparableNativeJson(jsonClassification, dataType, engine)) return tokens;
  tokens.add('=');
  tokens.add('<>');
  return tokens;
}

/** True for the native JSON types that have no equality operator. */
function isIncomparableNativeJson(
  jsonClassification: JsonClassification,
  dataType: string | undefined,
  engine: DatabaseEngineId,
): boolean {
  if (jsonClassification.kind === 'native-jsonb') return false;
  // Oracle stored JSON in a CLOB/BLOB with an `IS JSON` check long before it
  // had a native type, and a LOB has no equality operator either
  // (ORA-00932: inconsistent datatypes … got CLOB).
  if (engine === 'oracle' && /\b[NB]?CLOB\b|\bBLOB\b/i.test(dataType ?? '')) return true;
  if (jsonClassification.kind !== 'native-json') return false;
  if (engine === 'oracle') return true;
  // Postgres: only the `json` spelling lacks equality — a `jsonb` column
  // classified `native-json` by a looser path still compares.
  return engine === 'pgsql' && !/\bjsonb\b/i.test(dataType ?? '');
}

/** Expression-operand analogue of `emitStandardComparisonOperatorsForColumn`. */
export function emitStandardComparisonOperatorsForType(
  out: CompletionCandidate[],
  resultSqlType: OperandLiteralSqlType,
  engine: DatabaseEngineId,
): void {
  emitStandardOperators(
    out,
    suggestOperatorsForOperand(
      { kind: 'expression', text: '', resultSqlType },
      toSqlEngine(engine),
    ),
  );
}

/**
 * Shared dedup + emit for a resolved standard-operator lattice. Removes the
 * keyword/standalone duplicates the keyword path already pushed for tokens the
 * lattice now owns (`BETWEEN`, `IN`, `IS NULL`, …) so each operator appears once
 * as the tier-1 snippet form, then emits the lattice ordered by `orderHint`.
 */
function emitStandardOperators(
  out: CompletionCandidate[],
  operators: readonly Operator[],
): void {
  if (operators.length === 0) return;
  const latticeTokens = new Set(operators.map((o) => o.token.toUpperCase()));
  for (let i = out.length - 1; i >= 0; i -= 1) {
    const c = out[i];
    if (c.kind === 'keyword' && latticeTokens.has(c.label.toUpperCase())) {
      out.splice(i, 1);
    }
  }
  operators.forEach((op, idx) => out.push(standardOperatorToCandidate(op, idx)));
}

/**
 * Build the tier-1 candidate for a standard comparison operator. Unlike the
 * JSON `operatorToCandidate` (which labels with the human form because tokens
 * like `@>` are cryptic), the standard operators label with the **token** —
 * users scan the suggest list for `=`, `<`, `<>`, and the token-as-label makes
 * typing `<` match `<`, `<=`, `<>` directly. The human form moves to `detail`.
 * `orderHint` (the lattice index) sorts the operators ahead of the unhinted
 * columns/tables also emitted at the slot, so the next-token operators lead.
 */
function standardOperatorToCandidate(op: Operator, orderHint: number): CompletionCandidate {
  return {
    label: op.token,
    insertText: op.snippetTemplate,
    isSnippet: true,
    kind: 'snippet',
    tier: 1,
    detail: op.label,
    filterText: op.token,
    orderHint,
  };
}

/**
 * Translate a single `Operator` into a `CompletionCandidate`. The snippet
 * template is emitted as `insertText` with `isSnippet: true` so Monaco
 * expands the `${N:placeholder}` syntax on accept. `filterText` uses the
 * literal token so Monaco's prefix filter accepts symbolic operator
 * starters (e.g. typing `@` matches `@>`).
 */
function operatorToCandidate(op: Operator): CompletionCandidate {
  return {
    label: op.label,
    insertText: op.snippetTemplate,
    isSnippet: true,
    kind: 'snippet',
    tier: 6,
    detail: op.token + (op.requiresPath ? '  (needs JSON path)' : ''),
    filterText: op.token,
  };
}

const STANDARD_TOKENS: ReadonlySet<string> = new Set([
  '=',
  '<>',
  '<',
  '>',
  '<=',
  '>=',
  'IS NULL',
  'IS NOT NULL',
  'BETWEEN',
  'IN',
  'NOT IN',
  'LIKE',
]);

/**
 * Skip standard comparison operators (`=`, `<>`, `IS NULL`, etc.) from the
 * JSON lattice — those are already surfaced through the engine's keyword
 * set at expression positions. We only emit the *distinctive* JSON
 * operators here so the completion list doesn't double-count.
 */
function isStandardComparison(token: string): boolean {
  return STANDARD_TOKENS.has(token);
}
